'use client';

import Link from 'next/link';
import { ArrowUpRight, Instagram, Linkedin, Mail, MessageCircle, Phone } from 'lucide-react';

const ClientFooter = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about-us' },
      { label: 'Services', href: '/home' },
      { label: 'Contact', href: '/contact' },
      { label: 'Discuss', href: '/discuss' },
    ],
    resources: [
      { label: 'Blog', href: '/blogs' },
      { label: 'Freebies', href: '/freebies' },
      { label: 'Book Meeting', href: '/book-meeting' },
    ],
  };

  const socialLinks = [
    {
      name: 'WhatsApp',
      href: 'https://wa.me/919971066552?text=Hi%20Quirky%20Umbrella%2C%20I%20want%20to%20connect.',
      icon: MessageCircle,
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/quirkyumbrella/',
      icon: Instagram,
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/quirky-umbrella/',
      icon: Linkedin,
    },
    {
      name: 'Email',
      href: 'mailto:hello@quirkyumbrella.com?subject=Let%27s%20Connect',
      icon: Mail,
    },
  ];

  return (
    <footer className="relative mt-auto overflow-hidden border-t border-border/60 bg-gradient-to-br from-pink-50 via-slate-50 to-blue-50">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_20%,rgba(250,0,172,0.14),transparent_42%),radial-gradient(circle_at_84%_18%,rgba(39,78,255,0.12),transparent_40%),radial-gradient(circle_at_50%_90%,rgba(16,185,129,0.11),transparent_42%)]" />
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-10 py-10 md:py-12">
        <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-border/60 pb-7">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.26em] text-pink-600 mb-2">
              Let&apos;s Build Growth
            </p>
            <h3 className="font-heading text-2xl md:text-[2rem] font-bold text-foreground leading-tight">
              Ready to scale your brand with smarter systems?
            </h3>
          </div>
          <Link
            href="/book-meeting"
            className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-secondary px-5 py-3 text-sm font-bold text-white shadow-[0_18px_40px_-24px_rgba(250,0,172,0.65)] transition-all duration-300 hover:-translate-y-1"
          >
            Book Strategy Call
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-7 md:gap-8">
          <div className="xl:col-span-2">
            <Link
              href="/home"
              className="inline-flex items-center gap-3 mb-4 transition-opacity hover:opacity-90"
            >
              <img
                src="/assets/images/ImageToStl.com_QU_Logo-1768912371539.png"
                alt="Quirky Umbrella Logo"
                className="h-11 w-auto"
              />
              <span className="font-heading text-xl font-semibold text-foreground">
                Quirky Umbrella
              </span>
            </Link>

            <p className="text-muted-foreground max-w-xl leading-relaxed mb-5">
              We help brands turn ideas into sustainable growth through intelligent systems, strong
              strategy, and measurable execution.
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-5">
              <a
                href="mailto:hello@quirkyumbrella.com"
                className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <Mail className="w-4 h-4 text-primary" />
                hello@quirkyumbrella.com
              </a>
              <a
                href="tel:+919971066552"
                className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <Phone className="w-4 h-4 text-secondary" />
                +91 9971066552
              </a>
            </div>

            <div className="flex items-center gap-2.5">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target={social.name === 'Email' ? undefined : '_blank'}
                  rel={social.name === 'Email' ? undefined : 'noopener noreferrer'}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-white/80 text-foreground transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:text-primary"
                  aria-label={social.name}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-primary">
              Company
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-3 text-xs font-black uppercase tracking-[0.2em] text-secondary">
              Resources
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-border/60 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <p>&copy; 2024 Quirky Umbrella. All rights reserved.</p>
          {/* <p>&copy; {currentYear} Quirky Umbrella. All rights reserved.</p> */}
          <p>Built for brands that value strategy and measurable growth.</p>
        </div>
      </div>
    </footer>
  );
};

export default ClientFooter;
